import { useState } from "react";
import { Heart, ExternalLink, FileText, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/Reveal";
import { Section, Eyebrow, PageHero } from "@/components/common";
import { QRCode } from "@/components/QRCode";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";

export default function Donate() {
  const { data: content } = useContent();
  const [custom, setCustom] = useState("");
  if (!content) return <div className="h-screen" />;
  const d = content.donate || {};
  const base = d.actblue_url || "https://secure.actblue.com/donate/satish-chapagain";
  const amounts = d.amounts || [25, 50, 100, 250, 500];

  const donateLink = (amt) => (amt ? `${base}?amount=${amt}` : base);
  const open = (amt) => {
    trackEvent("donate_click", { location: "donate_page", amount: amt || "other" });
    window.open(donateLink(amt), "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <PageHero eyebrow="Donate" title="Power this campaign" subtitle="We don't take a dime from corporate PACs. This campaign is powered entirely by neighbors like you." />

      <Section className="py-16">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* APPEAL + AMOUNTS */}
          <Reveal className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 sm:p-9">
              <p className="text-lg text-slate-700 leading-relaxed font-serif italic">"{d.appeal}"</p>
              <p className="mt-3 font-serif font-bold text-navy">— Satish Chapagain</p>

              <div className="mt-8">
                <Eyebrow>Choose an amount</Eyebrow>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                  {amounts.map((a) => (
                    <button key={a} onClick={() => open(a)}
                      className="border-2 border-donate/30 text-donate font-serif font-bold text-xl rounded-xl py-5 hover:bg-donate hover:text-white transition-colors"
                      data-testid={`donate-amount-${a}`}>
                      ${a}
                    </button>
                  ))}
                  <button onClick={() => open(null)}
                    className="border-2 border-donate/30 text-donate font-semibold rounded-xl py-5 hover:bg-donate hover:text-white transition-colors"
                    data-testid="donate-amount-any">
                    Any Amount
                  </button>
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <Input type="number" min="1" placeholder="Custom amount ($)" value={custom} onChange={(e) => setCustom(e.target.value)} className="flex-1" data-testid="donate-custom-input" />
                  <Button onClick={() => open(custom || null)} className="bg-donate hover:bg-donate-hover text-white font-semibold h-11 px-8" data-testid="donate-custom-btn">
                    <Heart className="w-5 h-5 mr-2" /> Contribute
                  </Button>
                </div>

                <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
                  <ShieldCheck className="w-5 h-5 text-brightblue shrink-0" />
                  <span>All contributions are securely processed by <strong>ActBlue</strong>. This site never stores your payment information.</span>
                </div>
                <a href={base} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center text-brightblue font-semibold hover:text-brightblue-hover text-sm" data-testid="donate-actblue-link">
                  Go to our ActBlue page <ExternalLink className="w-4 h-4 ml-1.5" />
                </a>
              </div>
            </div>

            {/* CHECK NOTE */}
            <div className="mt-6 bg-sky/60 border border-sky-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-navy" />
                <h3 className="font-serif font-bold text-navy">Prefer to give by check?</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{d.check_note}</p>
            </div>
          </Reveal>

          {/* QR */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 text-center">
              <Eyebrow>Scan to Donate</Eyebrow>
              <p className="text-sm text-slate-600 mb-4">Scan with your phone or download for flyers and events.</p>
              <div className="flex justify-center">
                <QRCode data={base} label="Donate to Satish" filename="satish-donate" size={190} />
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
